import { ActionDto } from '@issuemanager/api-client';
import { useEventCallback } from '@mui/material';
import { UseFormReturn } from 'react-hook-form';
import { AssignIssueFormValues } from './use-manage-issue-form';
import { useIssueOutletContext } from './useIssueOutletContext';

type Params = {
  manageIssueForm: UseFormReturn<
    AssignIssueFormValues,
    any,
    AssignIssueFormValues
  >;
};

const mapActions = (actionsData: ActionDto[]) => {
  const owners: ActionDto['owner'][] = [];
  const approvers: ActionDto['approver'][] = [];

  const mappedActions = actionsData.map((action) => {
    const owner = action.owner ?? null;
    const approver = action.approver ?? null;

    if (owner) owners.push(owner);
    if (approver) approvers.push(approver);

    return {
      ...action,
      ownerId: owner?.id,
      approverId: approver?.id,
      // NOTE: govmapGroups aren't applied yet. Ticket RCP-12663
      govmapGroups: [],
    };
  });

  return mappedActions;
};

export const useResetFormData = ({
  manageIssueForm,
}: Params): { resetForm: () => void } => {
  const { issue } = useIssueOutletContext();

  const { data: issueData } = issue;

  const resetFormFromIssueData = useEventCallback(() => {
    if (!issueData) return;

    const actions = mapActions(issueData.actions);

    manageIssueForm.reset({
      title: issueData.title,
      description: issueData.description ?? '',
      issueType: issueData.issueType?.name,
      executionType: issueData.executionType,
      severity: issueData.severity,
      managerId: issueData.manager?.id,
      ownerId: issueData.owner?.id,
      approverId: issueData.approver?.id,
      dueDate: issueData.dueDate ?? undefined,
      govmapGroups: issueData.govmapGroups?.map((group) => group.id) ?? [],
      attachments: issueData.attachments,
      impact: issueData.impact ?? '',
      // RCP-31554: Use issueData.classification once it's added to the API
      classification: undefined,
      // RCP-31554: Use issueData.reviewStatus once it's added to the API
      reviewStatus: undefined,
      // RCP-31554: Use issueData.publishedDate once it's added to the API
      publishedDate: undefined,
      // RCP-31554: Use issueData.resolution once it's added to the API
      resolution: undefined,
      // RCP-31554: Use issueData.rootCause once it's added to the API
      rootCause: '',
      // RCP-31554: Use issueData.recommendations once it's added to the API
      recommendations: '',
      // RCP-31554: Use issueData.managementResponse once it's added to the API
      managementResponse: '',
      // RCP-31554: Use issueData.includeInAuditReport once it's added to the API
      includeInAuditReport: true,
      actions: actions.map(
        ({ owner: _owner, approver: _approver, ...action }) => ({
          ...action,
          customId: action.id,
        }),
      ),
    });
  });

  const resetForm = useEventCallback(() => {
    if (!issueData) return;
    resetFormFromIssueData();
  });

  return {
    resetForm,
  };
};

export default useResetFormData;
